// @ts-nocheck
/**
 * Educational Content Page
 * SEO-optimized page template for educational content with Workers edge rendering
 * Features semantic HTML, structured data, and Carbon Design System components
 */

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  Grid,
  Column,
  Tile,
  Tag,
  Button,
  Breadcrumb,
  BreadcrumbItem,
  Stack,
  Layer,
  SkeletonText,
  InlineNotification,
  TooltipDefinition,
  CodeSnippet,
  StructuredListWrapper,
  StructuredListHead,
  StructuredListBody,
  StructuredListRow,
  StructuredListCell,
} from '@carbon/react';
import {
  Share,
  Bookmark,
  Download,
  Printer,
  ChevronLeft,
  Time,
  User,
  Calendar,
  Analytics,
  CheckmarkFilled,
} from '@carbon/icons-react';
import { ContentGrid } from '../../components/content/ContentGrid';
import styles from './EducationalContentPage.module.scss';

interface EducationalContent {
  id: string;
  type: 'testimonial' | 'success-story' | 'demo-experience' | 'learning-outcome' | 'article' | 'guide';
  language: 'en' | 'pt';
  title: string;
  description: string;
  content: string;
  metadata?: {
    author?: string;
    institution?: string;
    tags?: string[];
    publishedAt?: string;
    updatedAt?: string;
    readingTime?: number;
    difficulty?: 'beginner' | 'intermediate' | 'advanced';
    targetAudience?: 'parents' | 'educators' | 'administrators' | 'all';
  };
  seoData?: {
    metaTitle?: string;
    metaDescription?: string;
    keywords?: string[];
    canonicalUrl?: string;
    ogImage?: string;
    structuredData?: any;
  };
  performance?: {
    views: number;
    shares: number;
    engagementRate: number;
    conversionRate: number;
    seoRanking?: number;
  };
}

interface PageMeta {
  title: string;
  description: string;
  keywords: string[];
  canonicalUrl: string;
  ogImage: string;
  structuredData: any;
}

export const EducationalContentPage: React.FC = () => {
  const { contentType, contentId } = useParams<{ contentType: string; contentId: string }>();
  const navigate = useNavigate();

  const [content, setContent] = useState<EducationalContent | null>(null);
  const [relatedContent, setRelatedContent] = useState<EducationalContent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [bookmarked, setBookmarked] = useState(false);
  const [shared, setShared] = useState(false);
  const [pageMeta, setPageMeta] = useState<PageMeta | null>(null);

  useEffect(() => {
    if (contentId) {
      fetchContent(contentId);
    } else {
      // Show content library/grid view
      setLoading(false);
    }
  }, [contentId]);

  const fetchContent = async (id: string) => {
    try {
      setLoading(true);
      setError(null);

      // Fetch specific content
      const response = await fetch(`/api/v1/educational-content/content/id/${id}`);
      if (!response.ok) {
        throw new Error('Content not found');
      }

      const result = await response.json();
      if (result.success && result.data) {
        const contentData = result.data;
        setContent(contentData);

        // Generate page meta from content and SEO data
        const meta = generatePageMeta(contentData);
        setPageMeta(meta);

        // Fetch related content
        await fetchRelatedContent(contentData.type, contentData.language, id);

        // Track page view for analytics
        trackPageView(id, contentData.title);
      } else {
        throw new Error(result.error || 'Failed to load content');
      }
    } catch (err) {
      console.error('Content fetch error:', err);
      setError(err instanceof Error ? err.message : 'Failed to load content');
    } finally {
      setLoading(false);
    }
  };

  const fetchRelatedContent = async (type: string, language: string, excludeId: string) => {
    try {
      const response = await fetch(`/api/v1/educational-content/content/${type}?lang=${language}&limit=6`);
      if (response.ok) {
        const result = await response.json();
        if (result.success && result.data) {
          // Filter out current content
          const related = result.data.filter((item: any) => item.id !== excludeId);
          setRelatedContent(related.slice(0, 3));
        }
      }
    } catch (error) {
      console.error('Related content fetch error:', error);
    }
  };

  const generatePageMeta = (content: EducationalContent): PageMeta => {
    const baseUrl = window.location.origin;
    const currentUrl = window.location.href;

    return {
      title: content.seoData?.metaTitle || content.title,
      description: content.seoData?.metaDescription || content.description,
      keywords: content.seoData?.keywords || content.metadata?.tags || [],
      canonicalUrl: content.seoData?.canonicalUrl || currentUrl,
      ogImage: content.seoData?.ogImage || `${baseUrl}/images/og-default.jpg`,
      structuredData: content.seoData?.structuredData || generateStructuredData(content),
    };
  };

  const generateStructuredData = (content: EducationalContent): any => {
    const baseUrl = window.location.origin;

    return {
      '@context': 'https://schema.org',
      '@type': 'EducationalOccupationalProgram',
      name: content.title,
      description: content.description,
      inLanguage: content.language,
      educationalLevel: content.metadata?.difficulty || 'All Levels',
      timeRequired: content.metadata?.readingTime ? `PT${content.metadata.readingTime}M` : 'PT5M',
      author: content.metadata?.author
        ? {
            '@type': 'Person',
            name: content.metadata.author,
          }
        : undefined,
      publisher: {
        '@type': 'Organization',
        name: 'Sigma08',
        url: baseUrl,
        logo: {
          '@type': 'ImageObject',
          url: `${baseUrl}/images/logo.png`,
        },
      },
      datePublished: content.metadata?.publishedAt,
      dateModified: content.metadata?.updatedAt,
      about: {
        '@type': 'Thing',
        name: 'Educational Technology',
      },
      audience: {
        '@type': 'EducationalAudience',
        educationalRole: content.metadata?.targetAudience || 'all',
      },
    };
  };

  const trackPageView = async (contentId: string, title: string) => {
    try {
      await fetch('/api/v1/analytics/events/privacy', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          eventType: 'page_view',
          contentId,
          contentTitle: title,
          pageUrl: window.location.href,
          timestamp: new Date().toISOString(),
        }),
      });
    } catch (error) {
      console.error('Page view tracking error:', error);
    }
  };

  const handleShare = async () => {
    if (navigator.share && content) {
      try {
        await navigator.share({
          title: content.title,
          text: content.description,
          url: window.location.href,
        });
        setShared(true);
        trackShareEvent();
      } catch (error) {
        console.error('Share error:', error);
        fallbackShare();
      }
    } else {
      fallbackShare();
    }
  };

  const fallbackShare = () => {
    // Copy URL to clipboard
    navigator.clipboard.writeText(window.location.href);
    setShared(true);
    trackShareEvent();
    setTimeout(() => setShared(false), 3000);
  };

  const trackShareEvent = async () => {
    if (!content) return;

    try {
      await fetch('/api/v1/analytics/events/privacy', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          eventType: 'share',
          contentId: content.id,
          contentTitle: content.title,
          timestamp: new Date().toISOString(),
        }),
      });
    } catch (error) {
      console.error('Share tracking error:', error);
    }
  };

  const handleBookmark = () => {
    setBookmarked(!bookmarked);
    // Save to localStorage or user account
    const bookmarks = JSON.parse(localStorage.getItem('bookmarked_content') || '[]');
    if (!bookmarked && content) {
      bookmarks.push(content.id);
    } else if (content) {
      const index = bookmarks.indexOf(content.id);
      if (index > -1) bookmarks.splice(index, 1);
    }
    localStorage.setItem('bookmarked_content', JSON.stringify(bookmarks));
  };

  // Safe content rendering function
  const renderContent = (contentHtml: string) => {
    // Parse content as structured data if it's JSON
    try {
      const structuredContent =
        typeof contentHtml === 'string' && contentHtml.startsWith('[') ? JSON.parse(contentHtml) : null;

      if (structuredContent && Array.isArray(structuredContent)) {
        return (
          <StructuredListWrapper>
            <StructuredListBody>
              {structuredContent.map((item, index) => (
                <StructuredListRow key={index}>
                  <StructuredListCell>{item.label || item.title || item.name || ''}</StructuredListCell>
                  <StructuredListCell>{item.value || item.content || item.description || ''}</StructuredListCell>
                </StructuredListRow>
              ))}
            </StructuredListBody>
          </StructuredListWrapper>
        );
      }
    } catch (e) {
      // If not JSON, treat as plain text
    }

    // For plain text or HTML-like content, sanitize and render safely
    const sanitizedContent = (contentHtml || '')
      .replace(/<script[^>]*>.*?<\/script>/gi, '')
      .replace(/<iframe[^>]*>.*?<\/iframe>/gi, '')
      .replace(/<object[^>]*>.*?<\/object>/gi, '')
      .replace(/<embed[^>]*>/gi, '')
      .replace(/on\w+\s*=\s*"[^"]*"/gi, '')
      .replace(/on\w+\s*=\s*'[^']*'/gi, '');

    // Convert basic HTML tags to React components
    const lines = sanitizedContent.split('\n');
    return (
      <Stack gap={4}>
        {lines
          .map((line, index) => {
            if (line.trim().startsWith('<h1>')) {
              return (
                <h1 key={index} className="cds--type-heading-05">
                  {line.replace(/<\/?h1>/g, '')}
                </h1>
              );
            } else if (line.trim().startsWith('<h2>')) {
              return (
                <h2 key={index} className="cds--type-heading-04">
                  {line.replace(/<\/?h2>/g, '')}
                </h2>
              );
            } else if (line.trim().startsWith('<h3>')) {
              return (
                <h3 key={index} className="cds--type-heading-03">
                  {line.replace(/<\/?h3>/g, '')}
                </h3>
              );
            } else if (line.trim().startsWith('<code>')) {
              return (
                <CodeSnippet key={index} type="inline">
                  {line.replace(/<\/?code>/g, '')}
                </CodeSnippet>
              );
            } else if (line.trim()) {
              return (
                <p key={index} className="cds--type-body-01">
                  {line.replace(/<\/?[^>]+(>|$)/g, '')}
                </p>
              );
            }
            return null;
          })
          .filter(Boolean)}
      </Stack>
    );
  };

  const getDifficultyColor = (difficulty?: string) => {
    // Using Carbon semantic color types
    switch (difficulty) {
      case 'beginner':
        return 'green';
      case 'intermediate':
        return 'blue';
      case 'advanced':
        return 'purple';
      default:
        return 'gray';
    }
  };

  const getContentTypeColor = (type: string) => {
    // Using Carbon semantic color types
    switch (type) {
      case 'article':
        return 'blue';
      case 'guide':
        return 'green';
      case 'testimonial':
        return 'purple';
      case 'success-story':
        return 'teal';
      default:
        return 'gray';
    }
  };

  if (loading) {
    return (
      <div className={styles.contentPage}>
        <div className={styles.header}>
          <SkeletonText heading width="60%" />
          <SkeletonText width="40%" />
        </div>
        <div className={styles.content}>
          <SkeletonText paragraph lineCount={10} />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.contentPage}>
        <InlineNotification kind="error" title="Error loading content" subtitle={error} />
        <Button onClick={() => navigate('/content')} className={styles.browseButton}>
          Browse All Content
        </Button>
      </div>
    );
  }

  // Content library view (when no specific content ID)
  if (!content) {
    return (
      <div className={styles.contentPage}>
        <Helmet>
          <title>Educational Content Library | Sigma08</title>
          <meta
            name="description"
            content="Explore our comprehensive library of educational content including articles, guides, testimonials, and success stories."
          />
          <meta
            name="keywords"
            content="educational content, learning resources, teaching materials, education technology"
          />
        </Helmet>

        <main>
          <header className={styles.libraryHeader}>
            <h1 className="cds--type-heading-05">Educational Content Library</h1>
            <p className="cds--type-body-long-01">
              Discover educational resources, success stories, and expert insights to enhance learning experiences.
            </p>
          </header>

          <ContentGrid
            contentType={contentType}
            viewMode="grid"
            enableSEOOptimization={true}
            enableAnalytics={true}
            onContentSelect={content => navigate(`/content/${content.type}/${content.id}`)}
          />
        </main>
      </div>
    );
  }

  return (
    <div className={styles.contentPage}>
      {/* SEO Meta Tags */}
      {pageMeta && (
        <Helmet>
          <title>{pageMeta.title}</title>
          <meta name="description" content={pageMeta.description} />
          <meta name="keywords" content={pageMeta.keywords.join(', ')} />
          <link rel="canonical" href={pageMeta.canonicalUrl} />

          {/* Open Graph */}
          <meta property="og:title" content={pageMeta.title} />
          <meta property="og:description" content={pageMeta.description} />
          <meta property="og:image" content={pageMeta.ogImage} />
          <meta property="og:url" content={pageMeta.canonicalUrl} />
          <meta property="og:type" content="article" />

          {/* Twitter Card */}
          <meta name="twitter:card" content="summary_large_image" />
          <meta name="twitter:title" content={pageMeta.title} />
          <meta name="twitter:description" content={pageMeta.description} />
          <meta name="twitter:image" content={pageMeta.ogImage} />

          {/* Structured Data */}
          <script type="application/ld+json">{JSON.stringify(pageMeta.structuredData)}</script>
        </Helmet>
      )}

      {/* Breadcrumb Navigation */}
      <nav className={styles.breadcrumbWrapper} aria-label="Breadcrumb">
        <Breadcrumb>
          <BreadcrumbItem href="/content">Content Library</BreadcrumbItem>
          <BreadcrumbItem href={`/content/${content.type}`}>{content.type.replace('-', ' ')}</BreadcrumbItem>
          <BreadcrumbItem isCurrentPage>{content.title}</BreadcrumbItem>
        </Breadcrumb>
      </nav>

      {/* Main Content */}
      <main>
        <Grid>
          <Column sm={4} md={8} lg={12}>
            <article className={styles.mainContent}>
              {/* Article Header */}
              <header className={styles.articleHeader}>
                <div className={styles.metadata}>
                  <div className={styles.tags}>
                    <Tag type={getContentTypeColor(content.type)} size="sm">
                      {content.type.replace('-', ' ')}
                    </Tag>
                    <Tag type={content.language === 'en' ? 'blue' : 'green'} size="sm">
                      {content.language.toUpperCase()}
                    </Tag>
                    {content.metadata?.difficulty && (
                      <Tag type={getDifficultyColor(content.metadata.difficulty)} size="sm">
                        {content.metadata.difficulty}
                      </Tag>
                    )}
                  </div>

                  <div className={styles.actions}>
                    <Button
                      kind="ghost"
                      size="sm"
                      renderIcon={Share}
                      iconDescription="Share content"
                      onClick={handleShare}
                    >
                      {shared ? 'Copied!' : 'Share'}
                    </Button>
                    <Button
                      kind="ghost"
                      size="sm"
                      renderIcon={Bookmark}
                      iconDescription="Bookmark content"
                      onClick={handleBookmark}
                      className={bookmarked ? styles.bookmarked : ''}
                    >
                      {bookmarked ? 'Bookmarked' : 'Bookmark'}
                    </Button>
                  </div>
                </div>

                <h1 className="cds--type-heading-06">{content.title}</h1>

                <p className="cds--type-body-long-01">{content.description}</p>

                <div className={styles.authorInfo}>
                  <Stack orientation="horizontal" gap={5}>
                    {content.metadata?.author && (
                      <div className={styles.author}>
                        <User size={16} />
                        <span className="cds--type-label-01">{content.metadata.author}</span>
                      </div>
                    )}

                    {content.metadata?.publishedAt && (
                      <div className={styles.publishDate}>
                        <Calendar size={16} />
                        <span className="cds--type-label-01">
                          {new Date(content.metadata.publishedAt).toLocaleDateString()}
                        </span>
                      </div>
                    )}

                    {content.metadata?.readingTime && (
                      <div className={styles.readingTime}>
                        <Time size={16} />
                        <span className="cds--type-label-01">{content.metadata.readingTime} min read</span>
                      </div>
                    )}
                  </Stack>

                  {content.metadata?.tags && content.metadata.tags.length > 0 && (
                    <div className={styles.contentTags}>
                      {content.metadata.tags.map((tag, index) => (
                        <Tag key={index} type="outline" size="sm">
                          {tag}
                        </Tag>
                      ))}
                    </div>
                  )}
                </div>
              </header>

              {/* Article Content */}
              <div className={styles.articleContent}>{renderContent(content.content)}</div>

              {/* Content Footer */}
              <footer className={styles.articleFooter}>
                <div className={styles.engagement}>
                  <Stack orientation="horizontal" gap={4}>
                    <Button kind="tertiary" size="sm" renderIcon={Share} onClick={handleShare}>
                      Share this content
                    </Button>

                    {content.performance && (
                      <div className={styles.stats}>
                        <span className="cds--type-helper-text-01">{content.performance.views} views</span>
                        <span className="cds--type-helper-text-01">{content.performance.shares} shares</span>
                      </div>
                    )}
                  </Stack>
                </div>
              </footer>
            </article>
          </Column>

          {/* Sidebar */}
          <Column sm={4} md={8} lg={4}>
            <aside className={styles.sidebar}>
              {/* Related Content */}
              {relatedContent.length > 0 && (
                <Tile className={styles.relatedContent}>
                  <h3 className="cds--type-heading-03">Related Content</h3>
                  <Stack gap={4}>
                    {relatedContent.map(item => (
                      <div
                        key={item.id}
                        className={styles.relatedItem}
                        onClick={() => navigate(`/content/${item.type}/${item.id}`)}
                      >
                        <h4 className="cds--type-heading-01">{item.title}</h4>
                        <p className="cds--type-label-01">{item.description.substring(0, 100)}...</p>
                        <div className={styles.relatedMeta}>
                          <Tag type={getContentTypeColor(item.type)} size="sm">
                            {item.type}
                          </Tag>
                          {item.metadata?.readingTime && (
                            <span className="cds--type-helper-text-01">{item.metadata.readingTime} min</span>
                          )}
                        </div>
                      </div>
                    ))}
                  </Stack>
                </Tile>
              )}

              {/* Quick Actions */}
              <Tile className={styles.quickActions}>
                <h3 className="cds--type-heading-03">Quick Actions</h3>
                <Stack gap={3}>
                  <Button kind="tertiary" size="sm" renderIcon={Download} onClick={() => window.print()}>
                    Download PDF
                  </Button>
                  <Button kind="tertiary" size="sm" renderIcon={Printer} onClick={() => window.print()}>
                    Print Article
                  </Button>
                  <Button
                    kind="tertiary"
                    size="sm"
                    renderIcon={Analytics}
                    onClick={() => navigate(`/analytics/content/${content.id}`)}
                  >
                    View Analytics
                  </Button>
                </Stack>
              </Tile>
            </aside>
          </Column>
        </Grid>
      </main>
    </div>
  );
};

export default EducationalContentPage;
